import { useId, useState } from 'react'
import { useStrings } from '../../../i18n'
import { POWER_STOPS, parsePowerInput, powerAtStop, stopForPower } from '../facets'

/** Stored value -> what the field shows. `null` shows the placeholder instead. */
const powerText = (powerKw: number | null): string => (powerKw === null ? '' : String(powerKw))

/**
 * The minimum-power constraint.
 *
 * The slider runs over stop *indices* rather than kilowatts so the thumb lands
 * on values the fleet actually has (see {@link POWER_STOPS}); `aria-valuetext`
 * carries the kilowatts, because "5 of 10" tells a screen-reader user nothing.
 *
 * Beside it sits the same number as a field, so a threshold the stops skip -
 * the 120 kW a particular car tapers at - can be typed rather than approximated.
 * The field is the readout: one number on screen, not a readout and an input
 * that can disagree. Typing snaps the thumb to the nearest stop through
 * {@link stopForPower} while the stored value stays exactly what was typed.
 */
export const PowerSlider = ({
  minPowerKw,
  onChange,
}: {
  minPowerKw: number | null
  onChange: (minPowerKw: number | null) => void
}) => {
  const strings = useStrings()
  const id = useId()

  /**
   * The field holds what is typed, the store holds what is meant, and they are
   * not the same thing while a key is still coming: `2.` and `-` are states you
   * pass through on the way to a number and neither may move the filter.
   * `stored` is how a change from elsewhere - the slider, clear-all, a pasted
   * link - is noticed, so the field follows the store without an effect.
   */
  const [draft, setDraft] = useState(() => powerText(minPowerKw))
  const [stored, setStored] = useState(minPowerKw)
  if (stored !== minPowerKw) {
    setStored(minPowerKw)
    setDraft(powerText(minPowerKw))
  }

  const readout =
    minPowerKw === null ? strings.filters.anyPower : `${minPowerKw} ${strings.filters.minPowerUnit}`
  const rejected = parsePowerInput(draft) === undefined

  const commit = (powerKw: number | null): void => {
    setStored(powerKw)
    setDraft(powerText(powerKw))
    onChange(powerKw)
  }

  const typePower = (raw: string): void => {
    setDraft(raw)
    const powerKw = parsePowerInput(raw)
    if (powerKw === undefined) return
    setStored(powerKw)
    onChange(powerKw)
  }

  return (
    <div className="facet facet--power">
      <label className="facet__legend" htmlFor={id}>
        {strings.filters.minPower}
      </label>

      <div className="power">
        <input
          className="power__slider"
          id={id}
          type="range"
          min={0}
          max={POWER_STOPS.length - 1}
          step={1}
          value={stopForPower(minPowerKw)}
          aria-valuetext={readout}
          onChange={(event) => commit(powerAtStop(Number(event.target.value)))}
        />

        <div className="power__exact">
          <input
            className="power__input"
            type="text"
            inputMode="numeric"
            autoComplete="off"
            aria-label={strings.filters.minPowerExact}
            aria-invalid={rejected}
            placeholder={strings.filters.anyPower}
            value={draft}
            // Half-typed text has said its piece by the time focus leaves; what
            // survives is the filter that is actually on.
            onBlur={() => setDraft(powerText(minPowerKw))}
            onChange={(event) => typePower(event.target.value)}
          />
          <span className="power__unit" aria-hidden="true">
            {strings.filters.minPowerUnit}
          </span>
        </div>
      </div>
    </div>
  )
}
